import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  RefreshCw, Users, Calendar, TrendingUp, Search,
  ChevronDown, ChevronUp, Trash2, X, ArrowLeft,
  Clock, Star, Euro, Gift, Plus, Send, QrCode, Trophy, Monitor, Music
} from 'lucide-react';
import PremiumPopup from './PremiumPopup';
import Avatar from './Avatar';
import DashboardTab from './DashboardTab';
import PushTab from './PushTab';
import SignageModal from './SignageModal';
import MusicControlModal from './MusicControlModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOLD = '#A9B2B9';
const COINS_PER_REWARD = 1000;

/* ─────────────────── helpers ─────────────────── */
function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('el-GR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('el-GR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}
function fmtTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
}

/* ─────────────────── Stat mini card ─────────────────── */
function MiniStat({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-white/10 p-4 space-y-1">
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-gray-600" />
        <p className="text-gray-500 text-xs font-light tracking-widest uppercase">{label}</p>
      </div>
      <p className="text-white text-2xl font-light">{value}</p>
      {sub && <p className="text-gray-600 text-xs font-light">{sub}</p>}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-white/10 p-6" data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-500 text-xs font-light tracking-widest uppercase mb-2">{label}</p>
          <p className="text-white text-3xl font-light">{value}</p>
          {sub && <p className="text-gray-600 text-xs mt-1 font-light">{sub}</p>}
        </div>
        <Icon className="w-5 h-5 text-gray-600" />
      </div>
    </div>
  );
}

/* ─────────────────── Delete confirmation ─────────────────── */
function DeleteConfirm({ user, onConfirm, onCancel }) {
  return (
    <div className="premium-modal-overlay fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="delete-confirm-modal">
      <div className="premium-modal-panel p-8 max-w-sm w-full space-y-6">
        <div className="premium-modal-divider" aria-hidden="true">
          <span className="premium-modal-divider-line" />
          <span className="premium-modal-divider-dot" />
          <span className="premium-modal-divider-line" />
        </div>
        <div>
          <p className="premium-modal-eyebrow mb-2">DIMAKAS Admin</p>
          <p className="premium-modal-heading text-sm mb-3">Διαγραφή Χρήστη</p>
          <p className="premium-modal-copy text-sm">
            Είστε σίγουροι ότι θέλετε να διαγράψετε τον χρήστη <span className="text-white">{user.name}</span>?
            Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onConfirm}
            className="premium-modal-danger flex-1"
            data-testid="confirm-delete-btn"
          >
            ΔΙΑΓΡΑΦΗ
          </button>
          <button
            onClick={onCancel}
            className="premium-modal-secondary flex-1"
            data-testid="cancel-delete-btn"
          >
            ΑΚΥΡΩΣΗ
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Coins Admin Section ─────────────────── */
function LoyaltyAdminSection({ userId }) {
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [deductOpen, setDeductOpen] = useState(false);
  const [addCoins, setAddCoins] = useState(50);
  const [addDesc, setAddDesc] = useState('');
  const [deductCoins, setDeductCoins] = useState(50);
  const [deductDesc, setDeductDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchLoyalty = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/loyalty`, { withCredentials: true });
      setLoyalty(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { fetchLoyalty(); }, [fetchLoyalty]);

  const handleAdd = async () => {
    setSubmitting(true); setMsg('');
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/loyalty/add`,
        { points: addCoins, description: addDesc || 'Χειροκίνητη προσθήκη coins' },
        { withCredentials: true });
      setMsg(`✓ ${res.data.message}`); setAddOpen(false); setAddDesc('');
      await fetchLoyalty();
    } catch (err) { setMsg(`✗ ${err.response?.data?.detail || 'Σφάλμα'}`); }
    finally { setSubmitting(false); }
  };

  const handleDeduct = async () => {
    setSubmitting(true); setMsg('');
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/loyalty/deduct`,
        { points: deductCoins, description: deductDesc || 'Χειροκίνητη αφαίρεση coins' },
        { withCredentials: true });
      setMsg(`✓ ${res.data.message}`); setDeductOpen(false); setDeductDesc('');
      await fetchLoyalty();
    } catch (err) { setMsg(`✗ ${err.response?.data?.detail || 'Σφάλμα'}`); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="text-gray-600 text-xs font-light py-4">Φόρτωση coins...</div>;

  const balance = loyalty?.balance ?? 0;

  return (
    <div className="border p-6 space-y-5" style={{ borderColor: GOLD + '30' }} data-testid="loyalty-admin-section">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Gift className="w-4 h-4" style={{ color: GOLD }} />
          <p className="text-xs font-light tracking-[0.3em] uppercase" style={{ color: GOLD }}>Coins</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setAddOpen(!addOpen); setDeductOpen(false); setMsg(''); }}
            className="flex items-center gap-1.5 border px-3 py-1.5 text-xs font-light tracking-widest transition-colors"
            style={{ borderColor: GOLD + '40', color: GOLD }}
            data-testid="add-points-btn">
            <Plus className="w-3 h-3" />
            ΠΡΟΣΘΗΚΗ
          </button>
          <button onClick={() => { setDeductOpen(!deductOpen); setAddOpen(false); setMsg(''); }}
            className="flex items-center gap-1.5 border px-3 py-1.5 text-xs font-light tracking-widest transition-colors"
            style={{ borderColor: 'rgba(255,80,80,0.4)', color: '#ff5050' }}
            data-testid="deduct-points-btn">
            <span className="text-xs leading-none">−</span>
            ΑΦΑΙΡΕΣΗ
          </button>
        </div>
      </div>

      {/* Add coins form */}
      {addOpen && (
        <div className="border border-white/10 p-4 space-y-3" data-testid="add-points-form">
          <p className="text-gray-500 text-xs font-light tracking-widest">Χειροκίνητη προσθήκη coins</p>
          <div className="flex gap-3 flex-wrap">
            <input type="number" value={addCoins} min={1}
              onChange={e => setAddCoins(Number(e.target.value))}
              className="w-24 bg-white/5 border border-white/15 text-white px-3 py-2 text-sm font-light focus:outline-none"
              data-testid="add-points-input" />
            <input type="text" placeholder="Λόγος (π.χ. promo, bonus)" value={addDesc}
              onChange={e => setAddDesc(e.target.value)}
              className="flex-1 min-w-0 bg-white/5 border border-white/15 text-white placeholder:text-gray-600 px-3 py-2 text-sm font-light focus:outline-none"
              data-testid="add-points-desc" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={submitting}
              className="text-xs border border-white/20 text-white px-4 py-2 font-light tracking-widest hover:border-white/50 transition-colors disabled:opacity-50"
              data-testid="add-points-submit">
              {submitting ? 'ΑΠΟΘΗΚΕΥΣΗ...' : 'ΑΠΟΘΗΚΕΥΣΗ'}
            </button>
            <button onClick={() => setAddOpen(false)}
              className="text-xs text-gray-600 px-3 py-2 font-light hover:text-gray-400 transition-colors">
              Ακύρωση
            </button>
          </div>
        </div>
      )}

      {/* Deduct coins form */}
      {deductOpen && (
        <div className="border p-4 space-y-3" style={{ borderColor: 'rgba(255,80,80,0.2)' }} data-testid="deduct-points-form">
          <p className="text-xs font-light tracking-widest" style={{ color: '#ff5050' }}>Χειροκίνητη αφαίρεση coins</p>
          <div className="flex gap-3 flex-wrap">
            <input type="number" value={deductCoins} min={1} max={balance || 9999}
              onChange={e => setDeductCoins(Number(e.target.value))}
              className="w-24 bg-white/5 border border-white/15 text-white px-3 py-2 text-sm font-light focus:outline-none"
              data-testid="deduct-points-input" />
            <input type="text" placeholder="Λόγος (π.χ. διόρθωση, ακύρωση)" value={deductDesc}
              onChange={e => setDeductDesc(e.target.value)}
              className="flex-1 min-w-0 bg-white/5 border border-white/15 text-white placeholder:text-gray-600 px-3 py-2 text-sm font-light focus:outline-none"
              data-testid="deduct-points-desc" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleDeduct} disabled={submitting || !balance}
              className="text-xs px-4 py-2 font-light tracking-widest disabled:opacity-40 transition-colors"
              style={{ background: 'rgba(255,80,80,0.15)', color: '#ff5050', border: '1px solid rgba(255,80,80,0.3)' }}
              data-testid="deduct-points-submit">
              {submitting ? 'ΑΦΑΙΡΕΣΗ...' : 'ΑΦΑΙΡΕΣΗ'}
            </button>
            <button onClick={() => setDeductOpen(false)}
              className="text-xs text-gray-600 px-3 py-2 font-light hover:text-gray-400 transition-colors">
              Ακύρωση
            </button>
          </div>
        </div>
      )}

      {/* Balance display */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <p className="text-gray-600 text-xs font-light uppercase tracking-widest mb-1">Διαθέσιμα</p>
          <p className="text-3xl font-light" style={{ color: balance > 0 ? GOLD : '#666' }} data-testid="loyalty-points-display">
            {balance.toLocaleString()}
          </p>
          <p className="text-gray-600 text-xs font-light mt-0.5">coins</p>
        </div>
        <div>
          <p className="text-gray-600 text-xs font-light uppercase tracking-widest mb-1">Σύνολο ever</p>
          <p className="text-3xl font-light text-white" data-testid="loyalty-all-ever">
            {((loyalty?.all_ever_earned ?? loyalty?.total_earned) || 0).toLocaleString()}
          </p>
          <p className="text-gray-600 text-xs font-light mt-0.5">κερδισμένα ποτέ</p>
        </div>
      </div>

      {/* Feedback message */}
      {msg && (
        <p className={`text-xs font-light ${msg.startsWith('✓') ? '' : 'text-red-400'}`}
          style={msg.startsWith('✓') ? { color: GOLD } : {}}
          data-testid="loyalty-action-msg">
          {msg}
        </p>
      )}

      {/* Recent history */}
      {loyalty?.history?.length > 0 && (
        <div className="border-t pt-4 space-y-3" style={{ borderColor: GOLD + '15' }}>
          <div className="flex items-center justify-between">
            <p className="text-gray-600 text-xs font-light uppercase tracking-widest">Πλήρες Ιστορικό Κινήσεων</p>
            <span className="text-gray-600 text-xs">{loyalty.history.length} συναλλαγές</span>
          </div>
          
          {/* Scrollable history list - ALL transactions */}
          <div className="max-h-96 overflow-y-auto space-y-2 pr-2" style={{ scrollbarWidth: 'thin' }}>
            {loyalty.history.map((h, i) => {
              const isEarned = h.action === 'earned';
              const amount = h.balance ?? h.points ?? 0;
              const date = new Date(h.date);
              const isExpired = h.expires_at && new Date(h.expires_at) < new Date();
              
              return (
                <div 
                  key={h.id || i} 
                  className="flex items-start gap-3 py-2.5 px-3 border border-white/10 rounded hover:bg-white/5 transition-colors"
                  data-testid={`loyalty-history-item-${i}`}
                >
                  {/* Icon */}
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      isEarned ? 'bg-green-500/20' : 'bg-red-500/20'
                    }`}
                  >
                    {isEarned ? (
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    )}
                  </div>
                  
                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs font-medium truncate">
                          {h.description || (isEarned ? 'Κέρδισε coins' : 'Εξαργύρωση')}
                        </p>
                        <p className="text-gray-500 text-[11px] mt-0.5">
                          {date.toLocaleDateString('el-GR', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {isEarned && h.expires_at && (
                          <p className={`text-[10px] mt-1 ${isExpired ? 'text-red-400' : 'text-gray-600'}`}>
                            {isExpired ? '⚠️ Έληξαν' : `Λήγουν: ${new Date(h.expires_at).toLocaleDateString('el-GR')}`}
                          </p>
                        )}
                      </div>
                      
                      {/* Amount */}
                      <div className="text-right shrink-0">
                        <p className={`text-xs font-bold ${isEarned ? 'text-green-400' : 'text-red-400'}`}>
                          {isEarned ? '+' : '-'}{amount}
                        </p>
                        <p className="text-gray-600 text-[10px]">coins</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


/* ─────────────────── Spin History Section ─────────────────── */
function SpinHistorySection({ userId }) {
  const [spinHistory, setSpinHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const [msg, setMsg] = useState('');

  const fetchSpinHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/spin-history`, { withCredentials: true });
      setSpinHistory(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSpinHistory();
  }, [fetchSpinHistory]);

  const handleRedeem = async (spinIndex, reward) => {
    if (!window.confirm(`Είσαι σίγουρος ότι θέλεις να εξαργυρώσεις: ${reward};`)) return;
    
    setRedeeming(spinIndex);
    setMsg('');
    try {
      const res = await axios.post(
        `${API}/admin/users/${userId}/redeem-spin-reward`,
        { spin_index: spinIndex },
        { withCredentials: true }
      );
      setMsg(`✓ ${res.data.message}`);
      await fetchSpinHistory();
    } catch (err) {
      setMsg(`✗ ${err.response?.data?.detail || 'Σφάλμα'}`);
    } finally {
      setRedeeming(null);
    }
  };

  if (loading) return <div className="text-gray-600 text-xs font-light py-4">Φόρτωση spin history...</div>;

  const history = spinHistory?.history || [];
  const totalSpins = spinHistory?.total_spins || 0;

  if (history.length === 0) {
    return (
      <div className="border border-white/10 p-6" data-testid="spin-history-section">
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-4 h-4 text-purple-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          <p className="text-xs font-light tracking-[0.3em] uppercase text-purple-400">Spin The Wheel History</p>
        </div>
        <p className="text-gray-600 text-xs font-light">Δεν υπάρχει ιστορικό spins</p>
      </div>
    );
  }

  return (
    <div className="border border-purple-500/20 p-6 space-y-4" data-testid="spin-history-section">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-purple-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          <p className="text-xs font-light tracking-[0.3em] uppercase text-purple-400">Spin The Wheel History</p>
        </div>
        <p className="text-xs text-gray-600 font-light">
          Σύνολο: <span className="text-white">{totalSpins}</span> spins
          {history.length < totalSpins && <span className="ml-2">(Εμφάνιση {history.length} πιο πρόσφατων)</span>}
        </p>
      </div>

      {msg && (
        <p
          className={`text-xs font-light ${msg.startsWith('✓') ? 'text-purple-400' : 'text-red-400'}`}
          data-testid="spin-redeem-msg"
        >
          {msg}
        </p>
      )}

      {/* Spin history list */}
      <div className="space-y-2">
        {history.map((spin, idx) => {
          const reward = spin.reward || spin.prize_label || 'Άγνωστο';
          
          const rewardColor = 
            reward === 'Δωρεάν Κούρεμα' ? 'text-green-400' :
            reward === 'Δωρεάν Μούσι' ? 'text-blue-400' :
            reward === 'Έκπτωση 20%' ? 'text-yellow-400' :
            reward.includes('coins') || reward.includes('Coins') ? 'text-purple-400' :
            reward === 'Δοκίμασε ξανά' ? 'text-gray-500' :
            'text-gray-400';
          
          const rewardIcon = 
            reward === 'Δωρεάν Κούρεμα' ? '✂️' :
            reward === 'Δωρεάν Μούσι' ? '🧔' :
            reward === 'Έκπτωση 20%' ? '💰' :
            reward === 'Δοκίμασε ξανά' ? '🔄' :
            reward.includes('coins') || reward.includes('Coins') ? '🪙' :
            '❓';

          const canRedeem = !spin.redeemed && ['Δωρεάν Κούρεμα', 'Δωρεάν Μούσι', 'Έκπτωση 20%'].includes(reward);

          // Calculate actual index in the backend history array
          // Backend stores oldest-first, we display newest-first (reversed)
          // So: idx=0 (newest) -> actualIndex = total_spins - 1
          //     idx=1 -> actualIndex = total_spins - 2, etc.
          const actualIndex = spinHistory.total_spins - 1 - idx;

          const isNoReward = reward === 'Δοκίμασε ξανά';

          return (
            <div
              key={idx}
              className={`border p-3 flex items-center gap-4 ${
                spin.redeemed ? 'border-white/5 bg-white/2' : 
                isNoReward ? 'border-gray-700/30 bg-gray-900/20' :
                'border-purple-500/20 bg-purple-500/5'
              }`}
              data-testid={`spin-item-${idx}`}
            >
              {/* Reward icon and name */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-2xl">{rewardIcon}</span>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-light ${rewardColor}`}>
                    {reward}
                  </p>
                  <p className="text-xs text-gray-600 font-light">
                    {new Date(spin.timestamp).toLocaleString('el-GR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              {/* Status / Action */}
              <div className="flex items-center gap-3">
                {isNoReward ? (
                  <span className="text-xs text-gray-600 font-light">—</span>
                ) : spin.redeemed ? (
                  <div className="text-right">
                    <p className="text-xs text-green-400 font-light">✓ Redeemed</p>
                    {spin.redeemed_at && (
                      <p className="text-[10px] text-gray-600 font-light">
                        {new Date(spin.redeemed_at).toLocaleString('el-GR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                    {spin.redeemed_by_admin && (
                      <p className="text-[10px] text-gray-600 font-light">
                        από {spin.redeemed_by_admin}
                      </p>
                    )}
                  </div>
                ) : canRedeem ? (
                  <button
                    onClick={() => handleRedeem(actualIndex, reward)}
                    disabled={redeeming === actualIndex}
                    className="text-xs border px-3 py-1.5 font-light tracking-widest transition-colors disabled:opacity-50"
                    style={{ borderColor: 'rgba(168, 85, 247, 0.4)', color: '#a855f7' }}
                    data-testid={`redeem-btn-${idx}`}
                  >
                    {redeeming === actualIndex ? 'REDEEM...' : 'REDEEM'}
                  </button>
                ) : (
                  <span className="text-xs text-gray-600 font-light">Auto-added</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function CustomerProfileModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apptTab, setApptTab] = useState('all');
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyContent, setNotifyContent] = useState('');
  const [notifySending, setNotifySending] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/users/${userId}/stats`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleSendNotify = async () => {
    if (!notifyTitle.trim() || !notifyContent.trim()) return;
    setNotifySending(true);
    setNotifyMsg('');
    try {
      const res = await axios.post(
        `${API}/admin/users/${userId}/notify`,
        { title: notifyTitle, content: notifyContent },
        { withCredentials: true }
      );
      setNotifyMsg(`✓ ${res.data.message}`);
      setNotifyTitle('');
      setNotifyContent('');
      setNotifyOpen(false);
    } catch (err) {
      setNotifyMsg(`✗ ${err.response?.data?.detail || 'Σφάλμα αποστολής'}`);
    } finally {
      setNotifySending(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[90] bg-black flex items-center justify-center" data-testid="customer-profile-loading">
        <p className="text-gray-500 text-sm font-light tracking-widest">ΦΟΡΤΩΣΗ...</p>
      </div>
    );
  }
  if (!data) return null;

  const { user, stats, services, staff, appointments } = data;
  const now = new Date().toISOString();
  const upcoming = appointments.filter(a => (a.start_time || '') >= now);
  const past = appointments.filter(a => (a.start_time || '') < now);

  const displayList = apptTab === 'all' ? appointments :
    apptTab === 'upcoming' ? upcoming : past;

  return (
    <div className="fixed inset-0 z-[90] bg-black overflow-y-auto" data-testid="customer-profile-modal">
      <div className="container mx-auto px-6 py-8 max-w-4xl space-y-8">

        {/* Back */}
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-gray-500 hover:text-white transition-colors text-sm font-light tracking-widest"
          data-testid="profile-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          ΕΠΙΣΤΡΟΦΗ
        </button>

        {/* User header */}
        <div className="flex items-center gap-5 border-b border-white/10 pb-8">
          <Avatar user={user} size="md" className="w-16 h-16" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-white text-lg font-light tracking-wide">{user.name}</h2>
              {user.is_admin && <span className="text-xs bg-white text-black px-2 py-0.5 font-medium">ADMIN</span>}
              <span className="text-xs border border-white/20 text-gray-400 px-2 py-0.5 font-light uppercase tracking-widest">
                {user.auth_provider === 'google' ? 'Google' : 'Email'}
              </span>
            </div>
            <p className="text-gray-400 text-sm font-light mt-0.5">{user.email}</p>
            <p className="text-gray-600 text-xs font-light mt-1">Εγγραφή: {fmtDate(user.created_at)}</p>
          </div>
          {/* Send personal message button */}
          <button
            onClick={() => setNotifyOpen(o => !o)}
            className="flex items-center gap-2 border px-4 py-2 text-xs font-light tracking-widest transition-colors shrink-0"
            style={{ borderColor: GOLD + '50', color: GOLD }}
            data-testid="send-notify-btn"
          >
            <Send className="w-3.5 h-3.5" />
            ΜΗΝΥΜΑ
          </button>
        </div>

        {/* Personal notification form */}
        {notifyOpen && (
          <div className="border border-white/10 p-5 space-y-3 -mt-4" data-testid="notify-form">
            <p className="text-xs font-light tracking-[0.2em] uppercase" style={{ color: GOLD }}>
              Προσωπική ειδοποίηση → {user.name}
            </p>
            <input
              type="text"
              placeholder="Τίτλος (π.χ. Ειδική προσφορά για σένα!)"
              value={notifyTitle}
              onChange={e => setNotifyTitle(e.target.value)}
              className="w-full bg-white/5 border border-white/15 text-white placeholder:text-gray-600 px-4 py-2.5 text-sm font-light focus:outline-none focus:border-white/30"
              data-testid="notify-title-input"
            />
            <textarea
              placeholder="Μήνυμα (π.χ. Αυτόν τον μήνα -20% στο μούσι!)"
              value={notifyContent}
              onChange={e => setNotifyContent(e.target.value)}
              rows={3}
              className="w-full bg-white/5 border border-white/15 text-white placeholder:text-gray-600 px-4 py-2.5 text-sm font-light focus:outline-none focus:border-white/30 resize-none"
              data-testid="notify-content-input"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSendNotify}
                disabled={notifySending || !notifyTitle.trim() || !notifyContent.trim()}
                className="flex items-center gap-2 px-5 py-2 text-xs font-light tracking-widest transition-colors disabled:opacity-40"
                style={{ background: GOLD, color: '#000' }}
                data-testid="notify-send-btn"
              >
                <Send className="w-3 h-3" />
                {notifySending ? 'ΑΠΟΣΤΟΛΗ...' : 'ΑΠΟΣΤΟΛΗ'}
              </button>
              <button
                onClick={() => { setNotifyOpen(false); setNotifyMsg(''); }}
                className="px-5 py-2 text-xs font-light tracking-widest border border-white/20 text-white hover:border-white/50 transition-colors"
                data-testid="notify-cancel-btn"
              >
                ΑΚΥΡΩΣΗ
              </button>
            </div>
            {notifyMsg && (
              <p className={`text-xs font-light ${notifyMsg.startsWith('✓') ? '' : 'text-red-400'}`}
                style={notifyMsg.startsWith('✓') ? { color: GOLD } : {}}
                data-testid="notify-msg">
                {notifyMsg}
              </p>
            )}
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="user-stats-cards">
          <MiniStat icon={Calendar} label="Σύνολο" value={stats.total} sub="ραντεβού" />
          <MiniStat icon={TrendingUp} label="Αυτόν τον μήνα" value={stats.this_month} sub="ραντεβού" />
          <MiniStat icon={Clock} label="Τελευταία επίσκεψη" value={stats.last_visit ? fmtDate(stats.last_visit) : '—'} />
          <MiniStat icon={Euro} label="Σύνολο αξία" value={stats.total_spent > 0 ? `${stats.total_spent}€` : '—'} sub="όλες οι επισκέψεις" />
        </div>

        {/* Coins section */}
        <LoyaltyAdminSection userId={userId} />

        {/* Spin Wheel History section */}
        <SpinHistorySection userId={userId} />

        {/* Services breakdown */}
        {services.length > 0 && (
          <div className="border border-white/10 p-6 space-y-4" data-testid="services-breakdown">
            <p className="text-gray-500 text-xs font-light tracking-widest uppercase">Υπηρεσίες</p>
            <div className="space-y-3">
              {services.map((s, i) => (
                <div key={i} className="flex items-center gap-4" data-testid={`service-item-${i}`}>
                  <span className="text-gray-400 text-sm font-light flex-1 truncate">{s.name}</span>
                  <div className="flex-1 max-w-xs bg-white/5 h-1.5">
                    <div
                      className="bg-white h-1.5 transition-all duration-700"
                      style={{ width: `${Math.round((s.count / (services[0]?.count || 1)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-white text-sm font-light w-8 text-right shrink-0">{s.count}×</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Staff breakdown */}
        {staff.length > 0 && (
          <div className="border border-white/10 p-6 space-y-4" data-testid="staff-breakdown">
            <p className="text-gray-500 text-xs font-light tracking-widest uppercase">Barbier</p>
            <div className="space-y-3">
              {staff.map((s, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Star className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                  <span className="text-gray-400 text-sm font-light flex-1">{s.name}</span>
                  <span className="text-white text-sm font-light">{s.count} επισκέψεις</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Appointment history */}
        <div className="space-y-4" data-testid="appointment-history">
          <div className="flex items-center justify-between">
            <p className="text-gray-500 text-xs font-light tracking-widest uppercase">Ιστορικό Ραντεβού</p>
          </div>

          {/* Sub-tabs */}
          <div className="flex border-b border-white/10">
            {[
              ['all', `Όλα (${appointments.length})`],
              ['upcoming', `Προσεχή (${upcoming.length})`],
              ['past', `Περασμένα (${past.length})`],
            ].map(([id, label]) => (
              <button key={id} onClick={() => setApptTab(id)}
                className={`py-2 px-4 text-xs font-light tracking-widest transition-all border-b-2 -mb-px ${
                  apptTab === id ? 'border-white text-white' : 'border-transparent text-gray-500 hover:text-gray-300'
                }`}
                data-testid={`history-tab-${id}`}
              >
                {label.toUpperCase()}
              </button>
            ))}
          </div>

          {displayList.length === 0 ? (
            <p className="text-gray-600 text-sm font-light text-center py-8">Δεν υπάρχουν ραντεβού</p>
          ) : (
            <div className="space-y-2">
              {displayList.map((a, i) => {
                const isPast = (a.start_time || '') < now;
                return (
                  <div key={i} className="border border-white/10 px-5 py-4 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 hover:bg-white/3 transition-colors"
                    data-testid={`history-item-${i}`}>
                    <div>
                      <p className="text-white text-sm font-light">{a.service_name || '—'}</p>
                      <p className="text-gray-500 text-xs font-light">{a.staff_name || ''}</p>
                    </div>
                    <div>
                      <p className="text-gray-300 text-sm font-light">{fmtDateTime(a.start_time)}</p>
                      {a.end_time && <p className="text-gray-500 text-xs font-light">έως {fmtTime(a.end_time)}</p>}
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-4">
                      {a.price != null && a.price > 0 && (
                        <span className="text-gray-300 text-sm font-light">{a.price}€</span>
                      )}
                      <span className={`text-xs font-light px-2 py-0.5 border ${
                        isPast ? 'border-gray-700 text-gray-600' : 'border-white/30 text-white'
                      }`}>
                        {isPast ? 'ΠΕΡΑΣΜΕΝΟ' : 'ΠΡΟΣΕΧΕΣ'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Appointments Tab ─────────────────── */
function AppointmentsTab() {
  const [appointments, setAppointments] = useState({ upcoming: [], past: [] });
  const [tab, setTab] = useState('upcoming');
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/appointments`, { withCredentials: true });
      setAppointments(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/appointments/sync`, {}, { withCredentials: true });
      await fetchAppointments();
    } catch (err) { console.error(err); }
    finally { setSyncing(false); }
  };

  const filterList = (list) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(a =>
      (a.customer_name || '').toLowerCase().includes(q) ||
      (a.customer_email || '').toLowerCase().includes(q) ||
      (a.service_name || '').toLowerCase().includes(q)
    );
  };

  const currentList = filterList(tab === 'upcoming' ? appointments.upcoming : appointments.past);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
          <input type="text" placeholder="Αναζήτηση πελάτη, email, υπηρεσία..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full bg-white/5 border border-white/10 text-white placeholder:text-gray-600 pl-10 pr-4 py-2.5 text-sm font-light focus:outline-none focus:border-white/30 transition-colors"
            data-testid="appointment-search" />
        </div>
        <button onClick={handleSync} disabled={syncing}
          className="flex items-center space-x-2 border border-white/20 text-white px-5 py-2.5 text-xs font-light tracking-widest hover:border-white/50 transition-all disabled:opacity-50"
          data-testid="sync-appointments-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
          <span>{syncing ? 'ΣΥΓΧΡΟΝΙΣΜΟΣ...' : 'SYNC SETMORE'}</span>
        </button>
      </div>

      <div className="flex border-b border-white/10">
        {[['upcoming', `Προσεχή (${appointments.upcoming.length})`], ['past', `Περασμένα (${appointments.past.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`py-2.5 px-6 text-xs font-light tracking-widest transition-all border-b-2 -mb-px ${tab === id ? 'border-white text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
            data-testid={`appt-tab-${id}`}>
            {label.toUpperCase()}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-600 text-sm font-light">Φόρτωση...</div>
      ) : currentList.length === 0 ? (
        <div className="text-center py-16 text-gray-600 text-sm font-light" data-testid="no-appointments">
          {search ? 'Δεν βρέθηκαν ραντεβού' : 'Δεν υπάρχουν ραντεβού'}
        </div>
      ) : (
        <div className="space-y-2" data-testid="appointments-list">
          {currentList.map((appt, i) => {
            const id = appt.appointment_key || appt.setmore_key || i;
            const isOpen = expandedId === id;
            return (
              <div key={id} className="border border-white/10 hover:bg-white/3 transition-colors" data-testid={`appointment-item-${i}`}>
                <button className="w-full px-5 py-4 flex items-center justify-between text-left"
                  onClick={() => setExpandedId(isOpen ? null : id)}>
                  <div className="flex items-center gap-6 flex-1 min-w-0">
                    <div className="text-left min-w-0 flex-1">
                      <p className="text-white text-sm font-light truncate">{appt.customer_name || '—'}</p>
                      <p className="text-gray-500 text-xs font-light truncate">{appt.customer_email || ''}</p>
                    </div>
                    <div className="text-left hidden sm:block min-w-0">
                      <p className="text-gray-300 text-xs font-light">{appt.service_name || '—'}</p>
                      <p className="text-gray-600 text-xs font-light">{appt.staff_name || ''}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-gray-300 text-xs font-light">{fmtDate(appt.start_time)}</p>
                      <p className="text-gray-500 text-xs font-light">{fmtTime(appt.start_time)}</p>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-gray-600 ml-3 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-600 ml-3 shrink-0" />}
                </button>
                {isOpen && (
                  <div className="border-t border-white/5 px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div><p className="text-gray-600 text-xs font-light uppercase tracking-widest mb-1">Υπηρεσία</p><p className="text-white text-sm font-light">{appt.service_name || '—'}</p></div>
                    <div><p className="text-gray-600 text-xs font-light uppercase tracking-widest mb-1">Barbier</p><p className="text-white text-sm font-light">{appt.staff_name || '—'}</p></div>
                    <div><p className="text-gray-600 text-xs font-light uppercase tracking-widest mb-1">Ώρα</p><p className="text-white text-sm font-light">{fmtTime(appt.start_time)}{appt.end_time ? ` — ${fmtTime(appt.end_time)}` : ''}</p></div>
                    <div><p className="text-gray-600 text-xs font-light uppercase tracking-widest mb-1">Email</p><p className="text-white text-sm font-light break-all">{appt.customer_email || '—'}</p></div>
                    {appt.customer_phone && <div><p className="text-gray-600 text-xs font-light uppercase tracking-widest mb-1">Τηλέφωνο</p><p className="text-white text-sm font-light">{appt.customer_phone}</p></div>}
                    {appt.price != null && <div><p className="text-gray-600 text-xs font-light uppercase tracking-widest mb-1">Κόστος</p><p className="text-white text-sm font-light">{appt.price}€</p></div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────── Users Tab ─────────────────── */
function UsersTab({ onViewProfile }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.user_id);
    setDeleteTarget(null);
    try {
      await axios.delete(`${API}/admin/users/${deleteTarget.user_id}`, { withCredentials: true });
      setUsers(prev => prev.filter(u => u.user_id !== deleteTarget.user_id));
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  return (
    <>
      {deleteTarget && (
        <DeleteConfirm
          user={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
            <input type="text" placeholder="Αναζήτηση χρήστη..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full bg-white/5 border border-white/10 text-white placeholder:text-gray-600 pl-10 pr-4 py-2.5 text-sm font-light focus:outline-none focus:border-white/30 transition-colors"
              data-testid="users-search" />
          </div>
          <p className="text-gray-600 text-xs font-light ml-4 shrink-0">{filtered.length} χρήστες</p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-600 text-sm font-light">Φόρτωση...</div>
        ) : (
          <div className="border border-white/10" data-testid="users-table">
            {/* Header */}
            <div className="hidden sm:grid grid-cols-[2fr_2fr_1fr_1fr_auto] px-5 py-3 border-b border-white/5 gap-4">
              {['Όνομα', 'Email', 'Εγγραφή', 'Τύπος', ''].map((h, i) => (
                <p key={i} className="text-gray-600 text-xs font-light uppercase tracking-widest">{h}</p>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-10 text-gray-600 text-sm font-light">Δεν βρέθηκαν χρήστες</div>
            ) : filtered.map((u, i) => (
              <div key={u.user_id || i}
                className="grid grid-cols-1 sm:grid-cols-[2fr_2fr_1fr_1fr_auto] gap-2 sm:gap-4 px-5 py-3.5 border-b border-white/5 hover:bg-white/3 transition-colors items-center"
                data-testid={`user-row-${i}`}>

                {/* Name + avatar + online status */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="relative shrink-0">
                    {u.picture ? (
                      <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full border border-white/10" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center border border-white/10">
                        <span className="text-white text-xs font-light">{(u.name || '?')[0].toUpperCase()}</span>
                      </div>
                    )}
                    {u.is_online && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-black" data-testid={`online-dot-${i}`} />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-light truncate">{u.name || '—'}</p>
                    <div className="flex items-center gap-1.5">
                      {u.is_admin && <span className="text-xs bg-white text-black px-1.5 py-0.5 font-medium">ADMIN</span>}
                      {u.is_online ? (
                        <span className="text-green-400 text-[10px] font-light">Online</span>
                      ) : u.last_seen ? (
                        <span className="text-zinc-600 text-[10px] font-light">Τελ. σύνδεση: {new Date(u.last_seen).toLocaleString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      ) : null}
                    </div>
                  </div>
                </div>

                <p className="text-gray-400 text-sm font-light truncate">{u.email}</p>
                <p className="text-gray-500 text-xs font-light">{fmtDate(u.created_at)}</p>
                <p className="text-gray-500 text-xs font-light uppercase tracking-widest">
                  {u.auth_provider === 'google' ? 'Google' : 'Email'}
                </p>

                {/* Actions */}
                <div className="flex items-center gap-2 justify-end">
                  <button
                    onClick={() => onViewProfile(u.user_id)}
                    className="text-xs border border-white/20 text-gray-400 hover:text-white hover:border-white/50 px-3 py-1.5 transition-colors font-light tracking-widest"
                    data-testid={`view-profile-btn-${i}`}
                    title="Προβολή προφίλ"
                  >
                    ΠΡΟΦΙΛ
                  </button>
                  <button
                    onClick={() => setDeleteTarget(u)}
                    disabled={deletingId === u.user_id}
                    className="p-1.5 text-gray-600 hover:text-red-400 transition-colors disabled:opacity-30"
                    data-testid={`delete-user-btn-${i}`}
                    title="Διαγραφή χρήστη"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ─────────────────── QR Scanner Tab ─────────────────── */
function QRScannerTab() {
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const scannerRef = React.useRef(null);
  const html5QrRef = React.useRef(null);
  const scannerRunning = React.useRef(false);

  const safeStop = async () => {
    if (html5QrRef.current && scannerRunning.current) {
      scannerRunning.current = false;
      try { await html5QrRef.current.stop(); } catch (_) { /* already stopped */ }
    }
  };

  const startScanner = async () => {
    setError('');
    setResult(null);
    setScanning(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      await new Promise(r => setTimeout(r, 300));
      const scanner = new Html5Qrcode("qr-reader");
      html5QrRef.current = scanner;
      scannerRunning.current = true;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        async (decodedText) => {
          await safeStop();
          setScanning(false);
          handleScan(decodedText);
        },
        () => {}
      );
    } catch (err) {
      setError('Δεν ήταν δυνατή η πρόσβαση στην κάμερα');
      setScanning(false);
      scannerRunning.current = false;
    }
  };

  const stopScanner = async () => {
    await safeStop();
    setScanning(false);
  };

  React.useEffect(() => { return () => { safeStop(); }; }, []);

  const handleScan = async (code) => {
    if (processing) return;
    setProcessing(true);
    setError('');
    setResult(null);
    try {
      const upper = code.trim().toUpperCase();
      if (upper.startsWith('DKMK')) {
        const res = await axios.post(`${API}/coupons/scan`, { barcode: upper }, { withCredentials: true });
        setResult({ ...res.data, type: 'coupon' });
      } else {
        const res = await axios.post(`${API}/loyalty/scan-qr`, { code }, { withCredentials: true });
        setResult({ ...res.data, type: 'loyalty' });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα σάρωσης');
    } finally {
      setProcessing(false);
    }
  };

  const handleManualSubmit = () => {
    if (manualCode.trim()) handleScan(manualCode.trim());
  };

  return (
    <div className="space-y-6" data-testid="qr-scanner-tab">
      <div className="text-center space-y-2">
        <QrCode className="w-10 h-10 mx-auto" style={{ color: GOLD }} />
        <h3 className="text-white text-sm font-heading tracking-[0.15em] uppercase">QR Scanner</h3>
        <p className="text-zinc-500 text-xs font-light">Σκανάρετε QR code πελάτη για εξαργύρωση πόντων</p>
      </div>

      {/* Scanner area */}
      {scanning ? (
        <div className="space-y-3">
          <div id="qr-reader" ref={scannerRef} className="w-full max-w-[300px] mx-auto overflow-hidden rounded-lg" />
          <div className="flex justify-center">
            <button onClick={stopScanner}
              className="px-6 py-2 text-xs font-heading tracking-widest uppercase bg-white/5 border border-white/10 text-zinc-400 hover:text-white transition-colors"
              data-testid="stop-scanner-btn">
              ΑΚΥΡΩΣΗ
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-center">
          <button onClick={startScanner}
            className="px-8 py-3 text-xs font-heading tracking-[0.15em] uppercase transition-all hover:scale-105 active:scale-95"
            style={{ background: GOLD, color: '#000' }}
            data-testid="start-scanner-btn">
            ΑΝΟΙΓΜΑ ΚΑΜΕΡΑΣ
          </button>
        </div>
      )}

      {/* Manual code input */}
      <div className="border border-white/5 p-4 space-y-3">
        <p className="text-zinc-500 text-xs font-light">Ή εισάγετε κωδικό χειροκίνητα:</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="rdm_..."
            className="flex-1 bg-white/5 border border-white/10 text-white px-3 py-2 text-sm font-light focus:outline-none focus:border-white/30"
            data-testid="manual-code-input"
          />
          <button onClick={handleManualSubmit}
            disabled={!manualCode.trim() || processing}
            className="px-4 py-2 text-xs font-heading tracking-widest uppercase bg-white text-black disabled:opacity-50"
            data-testid="manual-scan-btn">
            ΣΑΡΩΣΗ
          </button>
        </div>
      </div>

      {/* Processing indicator */}
      {processing && (
        <div className="text-center py-4">
          <div className="w-6 h-6 border border-white/20 border-t-white/60 rounded-full animate-spin mx-auto" />
          <p className="text-zinc-500 text-xs font-light mt-2">Επεξεργασία...</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="border border-red-500/30 bg-red-500/10 p-4 text-center" data-testid="scan-error">
          <p className="text-red-400 text-sm font-light">{error}</p>
        </div>
      )}

      {/* Success */}
      {result && (
        <div className="border p-5 space-y-3 text-center" style={{ borderColor: GOLD + '50', background: GOLD + '10' }} data-testid="scan-success">
          <p className="text-lg" style={{ color: GOLD }}>&#10003;</p>
          <p className="text-white text-sm font-light">{result.message}</p>
          {result.type === 'coupon' && (
            <div className="flex justify-center gap-6 text-xs font-light">
              <div><p className="text-zinc-500">Πελάτης</p><p className="text-white">{result.user_name}</p></div>
              <div><p className="text-zinc-500">Αξία</p><p style={{ color: GOLD }}>{result.coupon_value}</p></div>
            </div>
          )}
          {result.type === 'loyalty' && (
            <div className="flex justify-center gap-6 text-xs font-light">
              <div><p className="text-zinc-500">Πελάτης</p><p className="text-white">{result.user_name}</p></div>
              <div><p className="text-zinc-500">Αφαιρέθηκαν</p><p style={{ color: GOLD }}>{result.points_deducted} coins</p></div>
              <div><p className="text-zinc-500">Υπόλοιπο</p><p className="text-white">{result.remaining_points} coins</p></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────── Emergency Cuts Tab ─────────────────── */
function EmergencyCutsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [processing, setProcessing] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/emergency-cuts`, { withCredentials: true });
      setRequests(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleMarkCompleted = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.patch(`${API}/admin/emergency-cuts/${requestId}/complete`, {}, { withCredentials: true });
      await fetchRequests();
    } catch (err) {
      console.error('Failed to mark as completed:', err);
      alert('Σφάλμα κατά την ενημέρωση');
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = (requestId) => {
    setDeleteConfirm(requestId);
  };

  const confirmDelete = async () => {
    const requestId = deleteConfirm;
    setDeleteConfirm(null);
    setProcessing(requestId);
    try {
      await axios.delete(`${API}/admin/emergency-cuts/${requestId}`, { withCredentials: true });
      await fetchRequests();
    } catch (err) {
      console.error('Failed to delete:', err);
      alert('Σφάλμα κατά τη διαγραφή');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-gray-600 text-sm font-light">Φόρτωση...</div>;
  }

  return (
    <div className="space-y-4">
      <PremiumPopup
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        type="confirm"
        message="Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το αίτημα Emergency Cut;"
        onConfirm={confirmDelete}
        confirmText="ΔΙΑΓΡΑΦΗ"
        cancelText="ΑΚΥΡΩΣΗ"
      />
      <div className="flex items-center justify-between">
        <p className="text-gray-500 text-xs font-light">
          {requests.length} {requests.length === 1 ? 'αίτημα' : 'αιτήματα'}
        </p>
      </div>

      {requests.length === 0 ? (
        <div className="border border-white/10 p-12 text-center">
          <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30` }}>
            <span className="text-2xl" style={{ color: GOLD }}>⚡</span>
          </div>
          <p className="text-gray-500 text-sm font-light">Δεν υπάρχουν Emergency Cut requests</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req, i) => (
            <div key={req.request_id}
              className="border p-5 space-y-3"
              style={{ 
                borderColor: req.status === 'pending' ? `${GOLD}40` : 'rgba(255,255,255,0.1)',
                background: req.status === 'pending' ? `${GOLD}05` : 'transparent'
              }}
              data-testid={`emergency-request-${i}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white text-base font-light">{req.user_name}</h3>
                    {req.status === 'pending' ? (
                      <span className="text-xs px-2 py-0.5 border font-light"
                        style={{ color: GOLD, borderColor: `${GOLD}50`, background: `${GOLD}15` }}>
                        ΕΚΚΡΕΜΕΙ
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 border font-light text-green-400"
                        style={{ borderColor: 'rgba(74, 222, 128, 0.3)', background: 'rgba(74, 222, 128, 0.1)' }}>
                        ΟΛΟΚΛΗΡΩΘΗΚΕ
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600 font-light">Email:</span>
                      <a href={`mailto:${req.user_email}`} 
                        className="text-gray-300 font-light hover:text-white transition-colors">
                        {req.user_email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600 font-light">Τηλέφωνο:</span>
                      <a href={`tel:${req.user_phone}`}
                        className="font-light hover:text-white transition-colors"
                        style={{ color: GOLD }}>
                        {req.user_phone}
                      </a>
                    </div>
                  </div>
                  
                  <p className="text-gray-500 text-xs font-light">
                    {(() => {
                      // Convert UTC to Greece time (UTC+2 winter, UTC+3 summer)
                      const utcDate = new Date(req.created_at);
                      // Get Greece time using Intl API with explicit timezone
                      const greeceTime = new Intl.DateTimeFormat('el-GR', {
                        timeZone: 'Europe/Athens',
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      }).format(utcDate);
                      return greeceTime;
                    })()}
                  </p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  {req.status === 'pending' && (
                    <button
                      onClick={() => handleMarkCompleted(req.request_id)}
                      disabled={processing === req.request_id}
                      className="px-3 py-1.5 border text-xs font-light tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ 
                        borderColor: 'rgba(74, 222, 128, 0.3)', 
                        color: '#4ade80',
                        background: 'rgba(74, 222, 128, 0.05)'
                      }}
                    >
                      {processing === req.request_id ? 'PROCESSING...' : 'ΟΛΟΚΛΗΡΩΘΗΚΕ'}
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(req.request_id)}
                    disabled={processing === req.request_id}
                    className="px-3 py-1.5 border border-red-500/30 text-red-400 text-xs font-light tracking-widest hover:bg-red-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-4 h-4 mx-auto" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────── Inventory Tab (Stock Management Only) ─────────────────── */
function InventoryTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [search, setSearch] = useState('');
  const [adjustingStock, setAdjustingStock] = useState(null);
  const [msg, setMsg] = useState('');

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/inventory/products`, { withCredentials: true });
      setProducts(res.data.products || []);
      setLowStockCount(res.data.low_stock_count || 0);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    return !q || 
      (p.name || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q);
  });

  if (loading) return <div className="text-gray-600 text-xs font-light">Φόρτωση...</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-white text-sm font-light tracking-widest uppercase">Stock Management</h3>
        <p className="text-gray-600 text-xs font-light">
          Διαχείριση αποθέματος προϊόντων - Σύνολο: {products.length}
          {lowStockCount > 0 && <span className="text-red-400 ml-3">⚠️ {lowStockCount} χαμηλό stock</span>}
        </p>
      </div>

      {/* Message */}
      {msg && (
        <p className={`text-xs font-light ${msg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>
          {msg}
        </p>
      )}

      {/* Search */}
      <input
        type="text"
        placeholder="Αναζήτηση προϊόντος..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full bg-white/5 border border-white/10 px-4 py-2 text-white text-xs font-light placeholder-gray-600 focus:outline-none focus:border-white/20"
      />

      {/* Products Table */}
      {filtered.length === 0 ? (
        <p className="text-gray-600 text-xs font-light text-center py-8">
          {search ? 'Δεν βρέθηκαν προϊόντα' : 'Δεν υπάρχουν προϊόντα'}
        </p>
      ) : (
        <div className="border border-white/10 overflow-hidden">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr className="text-left border-b border-white/10">
                <th className="px-4 py-3 text-xs font-light tracking-widest uppercase text-gray-400">Προϊόν</th>
                <th className="px-4 py-3 text-xs font-light tracking-widest uppercase text-gray-400">Τιμή</th>
                <th className="px-4 py-3 text-xs font-light tracking-widest uppercase text-gray-400">Stock</th>
                <th className="px-4 py-3 text-xs font-light tracking-widest uppercase text-gray-400">Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((product) => {
                const isLowStock = product.stock <= product.low_stock_threshold;
                return (
                  <tr key={product.product_id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white text-sm font-light">{product.name}</p>
                        {product.category && (
                          <p className="text-gray-600 text-xs font-light">{product.category}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white text-sm font-light">{product.price.toFixed(2)}€</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-light ${isLowStock ? 'text-red-400' : 'text-white'}`}>
                        {product.stock}
                        {isLowStock && ' ⚠️'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setAdjustingStock(product)}
                        className="text-xs border border-blue-500/40 text-blue-400 px-3 py-1 hover:bg-blue-500/10"
                        title="Προσθαφαίρεση Stock"
                      >
                        +/- STOCK
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Adjust Stock Modal */}
      {adjustingStock && (
        <AdjustStockModal
          product={adjustingStock}
          onClose={() => setAdjustingStock(null)}
          onAdjust={async () => {
            setMsg('✓ Stock ενημερώθηκε επιτυχώς');
            await fetchProducts();
            setAdjustingStock(null);
            setTimeout(() => setMsg(''), 3000);
          }}
        />
      )}
    </div>
  );
}

// Adjust Stock Modal
function AdjustStockModal({ product, onClose, onAdjust }) {
  const [adjustment, setAdjustment] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(
        `${API}/admin/inventory/products/${product.product_id}/adjust-stock`,
        { adjustment: parseInt(adjustment), reason },
        { withCredentials: true }
      );
      onAdjust();
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα');
    }
  };

  const newStock = product.stock + parseInt(adjustment || 0);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-black border border-white/20 max-w-md w-full p-6">
        <h3 className="text-white text-sm font-light tracking-widest uppercase mb-4">
          Επεξεργασία Stock
        </h3>
        
        <div className="mb-4 bg-white/5 border border-white/10 p-3">
          <p className="text-white text-sm font-light mb-1">{product.name}</p>
          <p className="text-gray-400 text-xs">Τρέχον stock: <span className="text-white">{product.stock}</span></p>
          {adjustment && (
            <p className="text-blue-400 text-xs mt-1">
              Νέο stock: <span className="text-white font-medium">{newStock}</span>
            </p>
          )}
        </div>

        {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-400 text-xs font-light mb-1">
              Αλλαγή (+προσθήκη / -αφαίρεση)*
            </label>
            <input
              type="number"
              required
              value={adjustment}
              onChange={(e) => setAdjustment(e.target.value)}
              placeholder="π.χ. +10 ή -5"
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white text-sm"
              autoFocus
            />
            <p className="text-gray-600 text-xs mt-1">Χρησιμοποίησε + για προσθήκη, - για αφαίρεση</p>
          </div>
          
          <div>
            <label className="block text-gray-400 text-xs font-light mb-1">Λόγος (προαιρετικό)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="π.χ. Νέα παραλαβή, Πώληση, Απογραφή"
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white text-sm"
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className="flex-1 bg-white text-black px-4 py-2 text-xs font-light tracking-widest hover:bg-gray-200"
            >
              ΕΦΑΡΜΟΓΗ
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-white/20 text-white px-4 py-2 text-xs font-light tracking-widest hover:bg-white/5"
            >
              ΑΚΥΡΟ
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────── Main AdminPanel ─────────────────── */
export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('users');
  const [stats, setStats] = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);
  const [signageModalOpen, setSignageModalOpen] = useState(false);
  const [musicControlOpen, setMusicControlOpen] = useState(false);
  const [musicQRCode, setMusicQRCode] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { withCredentials: true })
      .then(res => setStats(res.data))
      .catch(err => console.error(err));
    
    // Fetch Music QR Code
    axios.get(`${API}/music/qr-code`, { withCredentials: true })
      .then(res => setMusicQRCode(res.data.qr_code))
      .catch(err => console.error('[MUSIC] Failed to fetch QR code:', err));
  }, []);

  const tabs = [
    { id: 'users', label: 'Χρήστες' },          // 1st
    { id: 'dashboard', label: 'Dashboard' },     // 2nd
    { id: 'inventory', label: 'Inventory' },
    { id: 'contest', label: 'Διαγωνισμός' },
    { id: 'qr-scanner', label: 'QR Scanner' },
    { id: 'push', label: 'Push' },               // NEW - before appointments
    { id: 'appointments', label: 'Ραντεβού' },   // Last
  ];

  return (
    <>
      {/* Customer profile overlay */}
      {profileUserId && (
        <CustomerProfileModal
          userId={profileUserId}
          onClose={() => setProfileUserId(null)}
        />
      )}

      {/* Digital Signage Modal */}
      <SignageModal 
        isOpen={signageModalOpen}
        onClose={() => setSignageModalOpen(false)}
      />

      {/* Music Control Modal */}
      <MusicControlModal
        isOpen={musicControlOpen}
        onClose={() => setMusicControlOpen(false)}
      />

      <div className="space-y-8" data-testid="admin-panel">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-white text-sm font-light tracking-[0.2em] uppercase mb-1">Admin Panel</h2>
            <p className="text-gray-600 text-xs font-light">Διαχείριση ραντεβού, χρηστών και στατιστικών</p>
          </div>
          
          {/* Quick Access Buttons */}
          <div className="flex gap-2">
            {/* QR Scanner Quick Access */}
            <button
              onClick={() => setActiveTab('qr-scanner')}
              className="group flex items-center gap-2 border px-4 py-2.5 transition-all hover:scale-105 active:scale-95"
              style={{ 
                borderColor: activeTab === 'qr-scanner' ? GOLD : 'rgba(201, 168, 76, 0.3)',
                background: activeTab === 'qr-scanner' ? `${GOLD}15` : 'transparent'
              }}
              title="Γρήγορη πρόσβαση στο QR Scanner"
              data-testid="qr-scanner-shortcut"
            >
              <QrCode 
                className="w-4 h-4 transition-colors" 
                style={{ color: activeTab === 'qr-scanner' ? GOLD : '#888' }}
              />
              <span 
                className="text-xs font-light tracking-widest transition-colors"
                style={{ color: activeTab === 'qr-scanner' ? GOLD : '#888' }}
              >
                QR SCANNER
              </span>
            </button>

            {/* Digital Signage Button */}
            <button
              onClick={() => setSignageModalOpen(true)}
              className="group flex items-center gap-2 border px-4 py-2.5 transition-all hover:scale-105 active:scale-95"
              style={{ 
                borderColor: 'rgba(201, 168, 76, 0.3)',
                background: 'transparent'
              }}
              title="Διαχείριση Digital Signage"
              data-testid="signage-button"
            >
              <Monitor 
                className="w-4 h-4 transition-colors" 
                style={{ color: '#888' }}
              />
              <span 
                className="text-xs font-light tracking-widest transition-colors text-gray-400 group-hover:text-white"
              >
                DIGITAL SIGNAGE
              </span>
            </button>

            {/* Music Control Button */}
            <button
              onClick={() => setMusicControlOpen(true)}
              className="group flex items-center gap-2 border px-4 py-2.5 transition-all hover:scale-105 active:scale-95"
              style={{ 
                borderColor: 'rgba(201, 168, 76, 0.3)',
                background: 'transparent'
              }}
              title="Έλεγχος Μουσικής"
              data-testid="music-control-button"
            >
              <Music 
                className="w-4 h-4 transition-colors" 
                style={{ color: '#888' }}
              />
              <span 
                className="text-xs font-light tracking-widest transition-colors text-gray-400 group-hover:text-white"
              >
                MUSIC CONTROL
              </span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto -mx-5 px-5 border-b border-white/10">
          <div className="flex min-w-max">
            {tabs.map(({ id, label }) => (
              <button key={id} onClick={() => setActiveTab(id)}
                className={`py-3 px-6 text-xs font-light tracking-widest transition-all border-b-2 -mb-px whitespace-nowrap ${
                  activeTab === id ? 'border-white text-white' : 'border-transparent text-gray-500 hover:text-gray-300'
                }`}
                data-testid={`admin-tab-${id}`}>
                {label.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div>
          {activeTab === 'appointments' && <AppointmentsTab />}
          {activeTab === 'users' && <UsersTab onViewProfile={setProfileUserId} />}
          {activeTab === 'inventory' && <InventoryTab />}
          {activeTab === 'contest' && <ContestAdminTab />}
          {activeTab === 'qr-scanner' && <QRScannerTab />}
          {activeTab === 'push' && <PushTab />}
          {activeTab === 'dashboard' && <DashboardTab />}
        </div>
      </div>
    </>
  );
}


/* ─────────────────── Contest Admin Tab ─────────────────── */
function ContestAdminTab() {
  const [entries, setEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [confirmDraw, setConfirmDraw] = useState(false);

  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const isLastDay = now.getDate() === lastDay;
  const currentMonth = now.toLocaleString('el-GR', { month: 'long', year: 'numeric' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [entriesRes, winnersRes] = await Promise.all([
        axios.get(`${API}/admin/contest/entries`, { withCredentials: true }),
        axios.get(`${API}/admin/contest/winners`, { withCredentials: true }),
      ]);
      setEntries(entriesRes.data.entries || []);
      setWinners(winnersRes.data.winners || []);
    } catch (e) {
      setError('Σφάλμα φόρτωσης');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDraw = async () => {
    setDrawing(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(`${API}/contest/draw`, {}, { withCredentials: true });
      setResult(res.data);
      setConfirmDraw(false);
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Σφάλμα κλήρωσης');
      setConfirmDraw(false);
    } finally {
      setDrawing(false);
    }
  };

  if (loading) return <div className="text-gray-600 text-xs font-light py-8 text-center">Φόρτωση...</div>;

  return (
    <div className="space-y-8" data-testid="contest-admin-tab">

      {/* Current Month Entries */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-white text-sm font-light tracking-widest uppercase">Συμμετοχές Μήνα</h3>
            <p className="text-gray-500 text-xs mt-0.5">{currentMonth} — {entries.length} συμμετέχοντες</p>
          </div>

          {/* Draw button — only on last day of month */}
          {isLastDay ? (
            !confirmDraw ? (
              <button
                onClick={() => setConfirmDraw(true)}
                disabled={entries.length === 0}
                className="flex items-center gap-2 px-5 py-2.5 text-xs font-light tracking-widest uppercase transition-all disabled:opacity-30"
                style={{ background: GOLD, color: '#000' }}
                data-testid="open-draw-btn"
              >
                <Trophy className="w-3.5 h-3.5" />
                ΚΛΗΡΩΣΗ
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs">Σίγουρος;</span>
                <button
                  onClick={handleDraw}
                  disabled={drawing}
                  className="px-4 py-2 text-xs font-light tracking-widest uppercase"
                  style={{ background: GOLD, color: '#000' }}
                  data-testid="confirm-draw-btn"
                >
                  {drawing ? 'ΚΛΗΡΩΝΩ...' : 'ΝΑΙ, ΚΛΗΡΩΣΗ'}
                </button>
                <button
                  onClick={() => setConfirmDraw(false)}
                  className="px-3 py-2 text-xs text-gray-500 hover:text-white transition-colors border border-white/10"
                  data-testid="cancel-draw-btn"
                >
                  ΑΚΥΡΩΣΗ
                </button>
              </div>
            )
          ) : (
            <div className="text-right">
              <p className="text-gray-600 text-xs">Κλήρωση διαθέσιμη</p>
              <p className="text-xs font-light" style={{ color: GOLD }}>{lastDay}/{now.getMonth() + 1}/{now.getFullYear()}</p>
            </div>
          )}
        </div>

        {/* Winner result */}
        {result && (
          <div className="border p-5 space-y-1" style={{ borderColor: `${GOLD}50`, background: `${GOLD}08` }} data-testid="draw-result">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-4 h-4" style={{ color: GOLD }} />
              <span className="text-xs font-light tracking-widest uppercase" style={{ color: GOLD }}>Νικητής Κλήρωσης</span>
            </div>
            <p className="text-white text-lg font-light">{result.winner.name}</p>
            <p className="text-gray-400 text-xs">{result.winner.email}</p>
            <p className="text-gray-600 text-xs mt-2">Από {result.total_entries} συμμετέχοντες</p>
          </div>
        )}

        {error && <p className="text-red-400 text-xs" data-testid="draw-error">{error}</p>}

        {/* Participants list */}
        {entries.length === 0 ? (
          <div className="border border-white/5 rounded-lg py-10 text-center">
            <Trophy className="w-8 h-8 mx-auto mb-3 opacity-20" style={{ color: GOLD }} />
            <p className="text-gray-500 text-xs">Δεν υπάρχουν συμμετοχές αυτόν τον μήνα</p>
          </div>
        ) : (
          <div className="border border-white/10 overflow-hidden rounded-lg" data-testid="contest-entries-list">
            <div className="grid grid-cols-3 px-4 py-2 border-b border-white/10 bg-white/3">
              <span className="text-gray-600 text-[10px] uppercase tracking-widest">#</span>
              <span className="text-gray-600 text-[10px] uppercase tracking-widest">Όνομα</span>
              <span className="text-gray-600 text-[10px] uppercase tracking-widest">Ημερομηνία</span>
            </div>
            {entries.map((entry, i) => (
              <div key={entry.entry_id || i} className="grid grid-cols-3 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/3 transition-colors"
                data-testid={`contest-entry-${i}`}>
                <span className="text-gray-600 text-xs font-mono">{i + 1}</span>
                <span className="text-white text-xs font-light">{entry.user_name || entry.user_email}</span>
                <span className="text-gray-500 text-xs">
                  {entry.entry_date ? new Date(entry.entry_date).toLocaleDateString('el-GR', { day: '2-digit', month: 'short' }) : '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past winners */}
      {winners.length > 0 && (
        <div className="space-y-3 border-t border-white/10 pt-6">
          <h3 className="text-gray-500 text-xs font-light tracking-widest uppercase">Ιστορικό Κληρώσεων</h3>
          <div className="space-y-2" data-testid="contest-winners-list">
            {winners.map((w, i) => (
              <div key={i} className="flex items-center justify-between py-3 px-4 border border-white/5 rounded-lg hover:border-white/10 transition-colors"
                data-testid={`contest-winner-${i}`}>
                <div>
                  <p className="text-white text-xs font-light">{w.winner_name}</p>
                  <p className="text-gray-600 text-[10px]">{w.winner_email}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-light" style={{ color: GOLD }}>{w.month}</p>
                  <p className="text-gray-600 text-[10px]">{w.total_entries} συμμετέχοντες</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
